/**
 * useCustomFormMetadata Hook
 *
 * Manages CustomFormMetaData for conditional questions functionality.
 * Handles creation, updates, and cleanup of metadata when fields change.
 */

import { CustomFormMetaData, IFormField } from '@ViewModels';
import * as React from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { ConditionalQuestionsConfig, SurveyFormData } from '../types';

/**
 * Hook for managing custom form metadata (conditional questions)
 */
export const useCustomFormMetadata = (
	config: ConditionalQuestionsConfig | undefined,
	setValue: UseFormSetValue<SurveyFormData>,
	watch: UseFormWatch<SurveyFormData>,
	fields: IFormField<string>[]
) => {
	// Watch the current metadata directly - don't memoize to ensure reactivity
	const watchedData = config?.metadataField ? watch(config.metadataField as keyof SurveyFormData) : null;
	const currentMetadata = React.useMemo((): CustomFormMetaData[] => {
		return Array.isArray(watchedData) ? watchedData : [];
	}, [watchedData]);

	// Get metadata for a specific field
	const getFieldMetadata = React.useCallback(
		(fieldId: string): CustomFormMetaData | undefined => {
			if (!config?.enabled || !Array.isArray(currentMetadata)) {
				return undefined;
			}
			return currentMetadata.find((meta: CustomFormMetaData) => meta.fieldId === fieldId);
		},
		[config?.enabled, currentMetadata]
	);

	// Handle conditional toggle for a field
	const handleConditionalToggle = React.useCallback(
		(fieldId: string, isConditional: boolean) => {
			if (!config?.enabled) return;

			const metadataArray = Array.isArray(currentMetadata) ? [...currentMetadata] : [];
			const existingIndex = metadataArray.findIndex((meta: CustomFormMetaData) => meta.fieldId === fieldId);

			if (isConditional) {
				// Enable conditional - add or update metadata
				const newMetadata: CustomFormMetaData = {
					fieldId,
					isConditional: true,
					ratings: [...config.defaultRatings],
				};

				if (existingIndex > -1) {
					metadataArray[existingIndex] = newMetadata;
				} else {
					metadataArray.push(newMetadata);
				}
			} else {
				// Disable conditional - remove metadata
				if (existingIndex > -1) {
					metadataArray.splice(existingIndex, 1);
				}
			}

			setValue(config.metadataField as 'customFormMetaData', metadataArray as CustomFormMetaData[]);
		},
		[config, currentMetadata, setValue]
	);

	// Handle rating threshold change for a field
	const handleRatingThresholdChange = React.useCallback(
		(fieldId: string, threshold: number) => {
			if (!config?.enabled) return;

			const metadataArray = Array.isArray(currentMetadata) ? [...currentMetadata] : [];
			const existingIndex = metadataArray.findIndex((meta: CustomFormMetaData) => meta.fieldId === fieldId);

			if (existingIndex > -1) {
				// Generate ratings array from 1 to threshold
				const ratings = Array.from({ length: threshold }, (_, index) => index + 1);
				metadataArray[existingIndex].ratings = ratings;
				setValue(config.metadataField as 'customFormMetaData', metadataArray as CustomFormMetaData[]);
			}
		},
		[config, currentMetadata, setValue]
	);

	// Clean up metadata when fields are removed - with guards to prevent infinite loops
	const fieldIds = React.useMemo(() => new Set(fields.map(field => field.id)), [fields]);

	React.useEffect(() => {
		if (!config?.enabled || !Array.isArray(currentMetadata) || currentMetadata.length === 0) return;

		const filteredMetadata = currentMetadata.filter((meta: CustomFormMetaData) => fieldIds.has(meta.fieldId));

		// Only update if metadata has changed to prevent infinite loops
		if (filteredMetadata.length !== currentMetadata.length && filteredMetadata.length < currentMetadata.length) {
			setValue(config.metadataField as 'customFormMetaData', filteredMetadata as CustomFormMetaData[]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldIds, config, setValue]); // Intentionally NOT including currentMetadata to prevent loops

	// Manual cleanup function for explicit calls
	const cleanupMetadata = React.useCallback(() => {
		if (!config?.enabled || !Array.isArray(currentMetadata)) return;

		const currentFieldIds = new Set(fields.map(field => field.id));
		const filteredMetadata = currentMetadata.filter((meta: CustomFormMetaData) => currentFieldIds.has(meta.fieldId));

		// Only update if metadata has changed
		if (filteredMetadata.length !== currentMetadata.length) {
			setValue(config.metadataField as 'customFormMetaData', filteredMetadata as CustomFormMetaData[]);
		}
	}, [config, currentMetadata, fields, setValue]);

	return {
		getFieldMetadata,
		handleConditionalToggle,
		handleRatingThresholdChange,
		cleanupMetadata,
		currentMetadata,
	};
};
